import React, { useRef, useState } from 'react';
import { Cloud, Feather, Flame, Gem, Mountain, Zap } from 'lucide-react';
import { mergeSpeciesResources, speciesUseProfile } from '../../../shared/rules/speciesResources.mjs';
import { getTotalClassLevel } from '../../../shared/rules/classIdentity.mjs';
import proficiencyBonus from '../../../utils/proficiencyBonus';
import { notify } from '../../../utils/notification';
import { spendSpeciesUse } from '../utils/speciesUseService';
import { GIANT_ANCESTRY_USE, giantAncestryKey } from '../../../shared/rules/speciesTraits.mjs';

// Species features used with a Bonus Action, as action-bar tiles (2024): Large Form (Goliath 5), Draconic Flight
// (Dragonborn 5) and Adrenaline Rush (Orc). The server spends the use, the Bonus Action on your turn and starts the effect.
const TILES = [
  { id: 'goliath-large-form', name: 'Large Form', icon: Mountain, accent: '#c48a4a', detail: 'Bonus Action · Large for 10 minutes, Advantage on Strength checks, +10 ft Speed' },
  { id: 'dragonborn-draconic-flight', name: 'Draconic Flight', icon: Feather, accent: '#5fb3ff', detail: 'Bonus Action · Fly Speed equal to your Speed for 10 minutes' },
  { id: 'orc-adrenaline-rush', name: 'Adrenaline Rush', icon: Zap, accent: '#ff7a45', detail: 'Bonus Action · Dash, and Temporary Hit Points equal to your Proficiency Bonus' },
  { id: 'dwarf-stonecunning', name: 'Stonecunning', icon: Gem, accent: '#a8a29e', detail: 'Bonus Action · Tremorsense 60 ft for 10 minutes (on or touching stone)' },
];

export default function SpeciesActionControls({ form, characterId, combatState, isActiveTurn, onCharacterChange, onCombatStateReconciled, onBreathWeapon, onCloudsJaunt }) {
  const [busy, setBusy] = useState(null);
  const requests = useRef({});
  const proficiency = Number.isFinite(Number(form?.proficiencyBonus)) ? Number(form.proficiencyBonus) : proficiencyBonus(getTotalClassLevel(form));
  const inCombat = Number.isInteger(combatState?.activeTurn) && Boolean(combatState?.participants?.[combatState.activeTurn]);
  const ledger = combatState?.turnActionLedger?.combatantId === String(characterId) && combatState.turnActionLedger.turnSequence === combatState.turnSequence ? combatState.turnActionLedger : null;
  const bonusReady = !inCombat || (isActiveTurn && !ledger?.bonusActionSpent);
  const use = async (tile) => {
    if (busy) return;
    setBusy(tile.id);
    try {
      const result = await spendSpeciesUse(String(form?._id || characterId), form, tile.id, requests.current);
      onCharacterChange?.((previous) => ({ ...mergeSpeciesResources(previous, result.speciesResources),
        ...(tile.id === 'orc-adrenaline-rush' && Number.isFinite(result.temporaryHitPoints) ? { temporaryHitPoints: result.temporaryHitPoints } : {}) }));
      if (result.combat) onCombatStateReconciled?.(result.combat);
      if (!result.replayed) notify(tile.id === 'orc-adrenaline-rush'
        ? `Adrenaline Rush · You Dash and have ${Number(result.temporaryHitPoints) || proficiency} Temporary Hit Points.`
        : `${tile.name} · Active for 10 minutes.`, 'success');
    } catch (error) { notify(error.message, 'warning'); }
    finally { setBusy(null); }
  };
  // Breath Weapon (Dragonborn): replaces one attack of the Attack action; the tile opens the Cone or Line choice.
  const breath = speciesUseProfile(form, 'dragonborn-breath-weapon', proficiency);
  const breathTile = breath.eligible && onBreathWeapon ? (
    <button key="dragonborn-breath-weapon" type="button" data-action-tile="dragonborn-breath-weapon" className="combat-hud-resource-tile combat-hud-feature-action-tile"
      style={{ '--hud-resource-accent': '#ff5a36' }} title="Breath Weapon · Replaces one attack · 15-foot Cone or 30-foot Line, Dexterity save"
      aria-label={`Breath Weapon: ${breath.remaining} of ${breath.maximum} uses`}
      disabled={Boolean(busy) || breath.remaining < 1 || (inCombat && !isActiveTurn)} onClick={() => onBreathWeapon()}>
      <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Flame size={20} /></span>
      <span className="combat-hud-resource-tile__name">Breath Weapon</span>
      <span className="combat-hud-resource-tile__value">{breath.remaining}/{breath.maximum}</span>
    </button>
  ) : null;
  // Cloud's Jaunt (Goliath, Cloud Giant): a Bonus Action teleport; the tile opens map targeting for the space.
  const ancestry = speciesUseProfile(form, GIANT_ANCESTRY_USE, proficiency);
  const jauntTile = giantAncestryKey(form) === 'cloud' && ancestry.eligible && onCloudsJaunt ? (
    <button key="clouds-jaunt" type="button" data-action-tile="clouds-jaunt" className="combat-hud-resource-tile combat-hud-feature-action-tile"
      style={{ '--hud-resource-accent': '#9ec9ff' }} title="Cloud's Jaunt · Bonus Action · Teleport up to 30 feet to an unoccupied space you can see"
      aria-label={`Cloud's Jaunt: ${ancestry.remaining} of ${ancestry.maximum} uses`}
      disabled={Boolean(busy) || ancestry.remaining < 1 || !bonusReady} onClick={() => onCloudsJaunt()}>
      <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Cloud size={20} /></span>
      <span className="combat-hud-resource-tile__name">Cloud&apos;s Jaunt</span>
      <span className="combat-hud-resource-tile__value">{ancestry.remaining}/{ancestry.maximum}</span>
    </button>
  ) : null;
  return <>{breathTile}{jauntTile}{TILES.map((tile) => {
    const profile = speciesUseProfile(form, tile.id, proficiency);
    if (!profile.eligible) return null;
    const Icon = tile.icon;
    return (
      <button key={tile.id} type="button" data-action-tile={tile.id} className="combat-hud-resource-tile combat-hud-feature-action-tile"
        style={{ '--hud-resource-accent': tile.accent }} title={`${tile.name} · ${tile.detail}`}
        aria-label={`${tile.name}: ${profile.remaining} of ${profile.maximum} uses`}
        disabled={Boolean(busy) || profile.remaining < 1 || !bonusReady} onClick={() => use(tile)}>
        <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Icon size={20} /></span>
        <span className="combat-hud-resource-tile__name">{busy === tile.id ? 'Saving…' : tile.name}</span>
        <span className="combat-hud-resource-tile__value">{profile.remaining}/{profile.maximum}</span>
      </button>
    );
  })}</>;
}
